import React from 'react';
import SeoLandingPage from './SeoLandingPage';

export default function AiVideoMakerLanding() {
  return (
    <SeoLandingPage
      canonicalPath="/ai-video-maker"
      title="Online AI Video Maker (No Download) | Merra AI Free Trial"
      description="Online AI video maker (no download) for short-form content. Import raw clips, generate scripts, hooks, voiceover, captions, music, and edits—then review, regenerate, and export for TikTok, Reels, and Shorts. Try Merra AI free."
      keywords="online AI video maker no download, AI video maker free no download, AI video generator, AI video editor online, free trial AI video maker"
      h1="Online AI video maker (no download)"
      subheading="Merra AI turns raw clips into ready-to-post short-form videos for creators and teams—online, with no download. Import footage, generate a script and hook, add voiceover, captions, music, and an edit—then review, regenerate if needed, and export for TikTok, Reels, and YouTube Shorts."
      mockDescriptionLabel="Description"
      mockDescriptionText="Import raw clips and generate a short-form video: hook + script, voiceover, captions, music, and a polished 9:16 edit you can review before export."
      seoArticleTitle="What an online AI video maker is for"
      seoArticleParagraphs={[
        'An online AI video maker (no download) is built for product-led evaluation: you want a clear workflow from raw footage to a publishable short, without installing a desktop editor. Merra AI is designed for that raw-clips-to-short-form use case.',
        'Creators, founders, coaches, and marketing teams use it to ship TikTok, Reels, and Shorts from the clips they already have. The workflow is intentional: generate a draft, review it carefully, regenerate when something needs to change, then export when it meets your standard.',
        'If you are searching for an AI video maker, AI video generator, or AI video editor online, focus on confirmed capabilities—automatic editing, batch creation, script and hook generation, voiceover, captions, music, raw-clip import, regeneration, and platform-ready exports—not unsupported promises.',
      ]}
      stepTitle="How the AI video maker workflow works"
      steps={[
        'Import raw clips into Merra AI.',
        'Generate a script and hook matched to your footage.',
        'Add AI voiceover for the narration track.',
        'Apply captions and music as part of the edit.',
        'Let Merra AI assemble the automatic edit and pacing.',
        'Review the draft carefully for accuracy, tone, and clarity.',
        'Regenerate script, voiceover, or editing when needed.',
        'Export a platform-ready 9:16 video for TikTok, Reels, or Shorts.',
      ]}
      audienceBullets={[
        'Creators who want consistent short-form output from phone footage',
        'Founders and marketers turning raw clips into publishable posts',
        'Teams that need a reviewable draft workflow—not timeline editing',
      ]}
      examples={[
        { title: 'Voiceover story', body: 'Turn a few raw clips into a narrated short with captions and music.' },
        { title: 'Product highlight', body: 'Explain one feature with a clear hook, script, and tight clip selection.' },
        { title: 'Founder update', body: 'Repurpose daily footage into consistent short-form marketing content.' },
      ]}
      testimonials={[
        { quote: 'What used to take hours now takes minutes. I just upload clips and ship.', by: 'Creator' },
        { quote: 'We can produce multiple videos per week without extra editing overhead.', by: 'Marketing team' },
        { quote: 'Perfect for voiceover-style storytelling. It feels like a superpower.', by: 'Founder' },
      ]}
      featureBullets={[
        'Raw-clip import for short-form video creation',
        'Script and hook generation',
        'AI voiceover, captions, and music integration',
        'Automatic editing with review and regeneration',
        'Batch creation from the same raw clips',
        'Exports for TikTok, Instagram Reels, and YouTube Shorts',
      ]}
      sections={[
        {
          title: 'Confirmed capabilities checklist',
          body: 'Merra AI’s online AI video maker workflow includes: automatic editing, batch creation, script and hook generation, voiceover, captions, music integration, raw-clip import, regeneration of generated elements, and exports for TikTok, Reels, and Shorts. Use this checklist when comparing tools so you evaluate confirmed features—not unsupported claims.',
        },
        {
          title: 'Built for creators and teams',
          body: 'The intended workflow is straightforward: import the clips you already shot, generate a short-form draft, review it, regenerate if needed, and export. Creators use it for personal brands and daily content. Teams use it to keep output consistent without adding editing headcount.',
        },
        {
          title: 'Try Merra AI free',
          body: 'You can try Merra AI free or start the free trial to run the online AI video maker workflow with your own raw clips. Review the generated draft, regenerate anything that needs attention, and export when you are ready to publish.',
        },
      ]}
      faqs={[
        {
          q: 'What is an online AI video maker?',
          a: 'An online AI video maker (no download) helps you turn raw footage into a short-form video draft in the browser. With Merra AI, that includes importing clips, generating a script and hook, adding voiceover, captions, music, and an automatic edit, then reviewing and regenerating before export.',
        },
        {
          q: 'Do I need to download software?',
          a: 'No. Merra AI works as an online AI video maker—you can create short-form videos in the browser with no download required.',
        },
        {
          q: 'Can I try Merra AI free?',
          a: 'Yes. You can try Merra AI free or start the free trial to evaluate the AI video maker workflow with your own clips.',
        },
        {
          q: 'What features does Merra AI include?',
          a: 'Confirmed capabilities include automatic editing, batch creation, script and hook generation, voiceover, captions, music integration, raw-clip import, regeneration, and exports for TikTok, Reels, and YouTube Shorts.',
        },
        {
          q: 'Do I still need to review the video?',
          a: 'Yes. Merra AI creates a first draft. You should review the hook, pacing, captions, voiceover, and overall accuracy before exporting and publishing.',
        },
        {
          q: 'What platforms can I export for?',
          a: 'Merra AI creates platform-ready short-form exports suitable for TikTok, Instagram Reels, and YouTube Shorts.',
        },
      ]}
      relatedLinks={[
        { to: '/ai-vlog-maker', label: 'AI vlog maker' },
        { to: '/ai-minivlog-maker', label: 'AI minivlog maker' },
        { to: '/ai-day-in-the-life-maker', label: 'AI day-in-the-life maker' },
        { to: '/ai-get-ready-with-me-maker', label: 'AI GRWM maker' },
        { to: '/blog/create-youtube-shorts-without-editing-skills', label: 'Create YouTube Shorts without editing skills' },
      ]}
    />
  );
}
